import { useRef, useState, useMemo } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  Sliders,
  Printer,
  PlusMinus,
  Warning,
  ChartBar,
  Camera,
  ArrowsHorizontal,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const itemAnim = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

// ---------------------------------------------------------------------------
// Interactive slider: applies a CSS brightness filter to a sample photo to
// simulate the visible effect of exposure compensation, and shows a small
// histogram that shifts left/right as EV changes.
// ---------------------------------------------------------------------------

// Approximate "what a +/- EV looks like" by combining brightness + contrast
// CSS filters. 1 EV ≈ 2× linear brightness, but we cap for visual sanity.
function filterForEV(ev) {
  // Map EV (-3..+3) to brightness multiplier (0.4..2.2) on a log-ish curve.
  const brightness = Math.pow(1.35, ev); // 0 EV = 1.0
  // A tiny contrast bump in negative EV keeps shadows from going flat-gray.
  const contrast = ev < 0 ? 1 + Math.min(0.15, -ev * 0.05) : 1;
  return `brightness(${brightness.toFixed(3)}) contrast(${contrast.toFixed(3)})`;
}

function MiniHistogram({ ev }) {
  // Single hump centered at 0.5, shifted left/right by EV.
  const N = 32;
  const center = Math.min(0.95, Math.max(0.05, 0.5 + ev * 0.13));
  const width = 0.18;
  const bars = [];
  for (let i = 0; i < N; i++) {
    const x = i / (N - 1);
    const v = Math.exp(-0.5 * ((x - center) / width) ** 2);
    bars.push(v);
  }
  const W = 280;
  const H = 70;
  const barW = W / N;
  const max = Math.max(...bars);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <rect x="0" y="0" width={W} height={H} fill="#0A0A0A" />
      {/* Walls */}
      <rect x="0" y="0" width="3" height={H} fill="#000000" />
      <rect x={W - 3} y="0" width="3" height={H} fill="#FFFFFF" />
      {bars.map((b, i) => {
        const h = (b / max) * (H - 12);
        return (
          <rect
            key={i}
            x={i * barW}
            y={H - 8 - h}
            width={barW * 0.92}
            height={h}
            fill="#F5A524"
          />
        );
      })}
      <line x1="0" y1={H - 8} x2={W} y2={H - 8} stroke="#52525B" strokeWidth="0.6" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Content (EN + ES)
// ---------------------------------------------------------------------------

const CONTENT = {
  en: {
    page_title: "Exposure Compensation: Taking Control of Auto Modes",
    page_sub:
      "Your camera's meter wants to make every photo 'middle gray.' That ruins snow scenes (gray slush) and dark stages (washed-out gloom). Exposure Compensation is the +/- override that puts YOU in charge. Master it and Aperture Priority becomes your most powerful tool.",
    download: "Download as PDF",
    generating: "Generating...",

    why_title: "The 'Why' — the 18% gray problem",
    why_body:
      "Every camera meter is calibrated to assume the average scene reflects 18% middle gray. This is a useful average for everyday shots — but it FAILS for any scene that is mostly bright (snow, beach, white walls) or mostly dark (night sky, dark stage, low-key portraits).",
    why_examples: [
      "Snow scene → meter sees TOO BRIGHT → darkens to gray → your snow becomes muddy gray. You must add +EV to put it back to white.",
      "Black tuxedo on dark background → meter sees TOO DARK → brightens → fabric becomes washed-out gray. You must subtract −EV to put it back to black.",
      "Backlit portrait → meter averages the bright background → face becomes a silhouette. Add +EV to brighten the face.",
      "Sunset → bright sky tricks the meter into underexposing the foreground. Add +EV (or use bracketing).",
    ],

    how_title: "The 'How' — universal +/- symbol",
    how_body:
      "Every modern camera has the same universal symbol: [+/−]. It is a button, a dial, or a menu setting. Pressing toward + (positive) makes the next photo BRIGHTER than the meter recommends. Pressing toward − (negative) makes the next photo DARKER. Adjustments are in EV stops (Exposure Values).",
    how_units: [
      "+1 EV ≈ doubles the light (one full stop brighter)",
      "+0.7 EV ≈ a third of a stop short of doubling",
      "+0.3 EV ≈ a small nudge — useful for fine tuning",
      "−1 EV ≈ halves the light (one full stop darker)",
      "Most cameras adjust in 1/3-stop increments: 0, +0.3, +0.7, +1.0, +1.3, ...",
    ],

    interactive_title: "Try it — interactive slider",
    interactive_intro:
      "Drag the slider below to see how Exposure Compensation visually affects an image. The simulation applies a brightness adjustment AND shifts the histogram horizontally — the same thing your camera does.",
    interactive_left: "Original (0 EV)",
    interactive_right: "With Exposure Comp.",
    interactive_caption: "Notice how the histogram pile shifts left or right with the EV value.",

    histogram_link_title: "The Histogram Connection",
    histogram_link_body:
      "If you read The Histogram lesson, you already know that data piled against the LEFT WALL = clipping (lost shadow detail) and data piled against the RIGHT WALL = clipping (blown highlights). Exposure Compensation is the FIX for both.",
    histogram_link_rules: [
      "Histogram piled against LEFT wall → add +EV → data shifts RIGHT, away from clipping",
      "Histogram piled against RIGHT wall → subtract −EV → data shifts LEFT, away from clipping",
      "Goal: keep the data inside the walls. Mood determines where inside.",
    ],
    histogram_link_warning:
      "Exposure Compensation does NOT 'fix' an already-clipped JPEG. It works on the NEXT shot. Always check the histogram after applying compensation and shoot again.",

    scenarios_title: "Real-world scenarios — quick reference",
    scenarios: [
      { name: "Bright snow / beach / white sand", ev: "+1 to +1.7 EV", body: "Keep the white WHITE, not gray. Push generously." },
      { name: "Backlit portrait (sun behind subject)", ev: "+1 to +2 EV", body: "Lift the face out of shadow. Watch background highlights." },
      { name: "Wedding bride in white dress, dark church", ev: "+0.7 to +1.3 EV", body: "Meter is fooled by the dark interior. Brighten so the dress stays white." },
      { name: "Concert / stage with spotlit performer", ev: "−1 to −2 EV", body: "Meter wants to brighten the dark stage; you want the dark stage. Subtract." },
      { name: "Sunset / golden hour", ev: "−0.3 to −0.7 EV", body: "Slight underexposure makes colors richer. Saves the sky from blowing out." },
      { name: "Black animal or dark fabric subject", ev: "−0.7 to −1.3 EV", body: "Keep the black BLACK, not gray." },
      { name: "Foggy / overcast misty scenes", ev: "+0.3 to +0.7 EV", body: "Fog reads as 'too bright' to the meter — bump up so the mood looks airy, not muddy." },
      { name: "Night sky / fireworks / silhouettes", ev: "−1 to −3 EV", body: "Embrace darkness. Let the meter's 'middle gray' bias go." },
    ],

    hardware_title: "How to apply +/− EV — by brand",
    hardware: [
      {
        name: "Canon",
        body: "Rotate the rear Quick Control dial while in Av, Tv, or P mode. Or press the dedicated [+/−] button + rotate the main dial. Look for the EV scale at the bottom of the viewfinder or LCD — it shows your current compensation.",
      },
      {
        name: "Nikon",
        body: "Hold the [+/−] button (next to the shutter release) and rotate the rear command dial. The current EV value appears in the top LCD and in the viewfinder. Some bodies have a dedicated EV dial on the top plate.",
      },
      {
        name: "Sony",
        body: "Most bodies have a dedicated exposure compensation DIAL on the top plate (locked: press the center button to unlock). Or assign EV to a custom button. Mirrorless cameras show a live preview that updates as you turn the dial — you literally see what the photo will look like.",
      },
      {
        name: "Fujifilm",
        body: "Most Fuji bodies have a dedicated EV dial on the top plate (sometimes marked with values −3 to +3). Some newer models offer a 'C' position that lets you set EV with the rear command dial up to ±5 EV.",
      },
    ],

    auto_mode_title: "Which modes does it work in",
    auto_mode_body: "Exposure Compensation only makes sense when the camera is choosing some part of the exposure for you:",
    auto_mode_table: [
      ["Program (P)", "Yes — camera picks aperture AND shutter; EV nudges both"],
      ["Aperture Priority (Av/A)", "Yes — you pick aperture, camera picks shutter; EV nudges shutter"],
      ["Shutter Priority (Tv/S)", "Yes — you pick shutter, camera picks aperture; EV nudges aperture"],
      ["Manual (M) with Auto-ISO", "Yes — camera picks ISO; EV nudges ISO. Powerful for events."],
      ["Manual (M) fixed ISO", "NO — you control everything already. Adjust aperture, shutter, or ISO directly."],
    ],

    pro_tip:
      "Pro tip: do not be afraid to use big values. +2 EV on snow is normal, not extreme. Beginners under-compensate because they fear ruining the shot. Trust the histogram, dial confidently, and reset to 0 between sessions so you don't accidentally underexpose your next family photo.",

    workflow_title: "Your daily workflow",
    workflow_steps: [
      "Set the camera to Aperture Priority (Av/A).",
      "Take a test shot.",
      "Check the histogram (and Blinkies).",
      "If clipping on left (lost shadows) → +EV. If clipping on right (lost highlights) → −EV.",
      "Re-shoot. Confirm the histogram looks correct for the scene mood.",
      "Reset to 0 EV when the lighting changes or you move to a new scene.",
    ],
  },

  es: {
    page_title: "Compensacion de Exposicion: Tomando el Control de los Modos Automaticos",
    page_sub:
      "El medidor de tu camara quiere hacer toda foto 'gris medio'. Eso arruina escenas de nieve (slush gris) y escenarios oscuros (penumbra lavada). La Compensacion de Exposicion es el override +/- que te pone a TI al mando. Domina esto y la Prioridad Apertura se vuelve tu herramienta mas poderosa.",
    download: "Descargar como PDF",
    generating: "Generando...",

    why_title: "El 'Por que' — el problema del gris al 18%",
    why_body:
      "Cada medidor de camara esta calibrado asumiendo que la escena promedio refleja gris medio al 18%. Es un promedio util para fotos cotidianas — pero FALLA en cualquier escena mayoritariamente brillante (nieve, playa, paredes blancas) o mayoritariamente oscura (cielo nocturno, escenario oscuro, retratos low-key).",
    why_examples: [
      "Escena nevada → medidor ve DEMASIADO BRILLANTE → oscurece a gris → tu nieve queda gris sucio. Debes sumar +EV para volverla blanca.",
      "Tuxedo negro sobre fondo oscuro → medidor ve DEMASIADO OSCURO → aclara → la tela queda gris lavado. Debes restar −EV para volverla negra.",
      "Retrato a contraluz → medidor promedia el fondo brillante → el rostro queda en silueta. Suma +EV para iluminar el rostro.",
      "Atardecer → el cielo brillante engaña al medidor a subexponer el primer plano. Suma +EV (o usa bracketing).",
    ],

    how_title: "El 'Como' — simbolo universal +/-",
    how_body:
      "Toda camara moderna tiene el mismo simbolo universal: [+/−]. Es un boton, un dial o un ajuste de menu. Moverlo hacia + (positivo) hace la proxima foto MAS BRILLANTE de lo que recomienda el medidor. Moverlo hacia − (negativo) la hace MAS OSCURA. Los ajustes son en pasos EV (Valores de Exposicion).",
    how_units: [
      "+1 EV ≈ duplica la luz (un paso completo mas brillante)",
      "+0.7 EV ≈ un tercio de paso antes de duplicar",
      "+0.3 EV ≈ un empujoncito — util para ajuste fino",
      "−1 EV ≈ reduce la luz a la mitad (un paso completo mas oscuro)",
      "La mayoria de las camaras ajusta en incrementos de 1/3 de paso: 0, +0.3, +0.7, +1.0, +1.3, ...",
    ],

    interactive_title: "Pruebalo — slider interactivo",
    interactive_intro:
      "Arrastra el slider abajo para ver como la Compensacion de Exposicion afecta visualmente una imagen. La simulacion aplica un ajuste de brillo Y desplaza el histograma horizontalmente — lo mismo que hace tu camara.",
    interactive_left: "Original (0 EV)",
    interactive_right: "Con Compensacion",
    interactive_caption: "Nota como la columna del histograma se desplaza a izquierda o derecha segun el valor EV.",

    histogram_link_title: "La Conexion con el Histograma",
    histogram_link_body:
      "Si leiste la leccion de El Histograma, ya sabes que los datos acumulados contra el MURO IZQUIERDO = clipping (detalle perdido en sombras) y contra el MURO DERECHO = clipping (altas luces quemadas). La Compensacion de Exposicion es el ARREGLO para ambos.",
    histogram_link_rules: [
      "Histograma acumulado contra muro IZQUIERDO → suma +EV → los datos se desplazan a la DERECHA, lejos del clipping",
      "Histograma acumulado contra muro DERECHO → resta −EV → los datos se desplazan a la IZQUIERDA, lejos del clipping",
      "Meta: mantener los datos dentro de los muros. El animo determina donde dentro.",
    ],
    histogram_link_warning:
      "La Compensacion de Exposicion NO 'arregla' un JPEG ya recortado. Funciona sobre el PROXIMO disparo. Siempre revisa el histograma despues de aplicar compensacion y vuelve a disparar.",

    scenarios_title: "Escenarios del mundo real — guia rapida",
    scenarios: [
      { name: "Nieve / playa / arena blanca brillante", ev: "+1 a +1.7 EV", body: "Mantener el blanco BLANCO, no gris. Empuja generosamente." },
      { name: "Retrato a contraluz (sol detras del sujeto)", ev: "+1 a +2 EV", body: "Levanta el rostro de la sombra. Vigila las altas luces del fondo." },
      { name: "Novia de vestido blanco en iglesia oscura", ev: "+0.7 a +1.3 EV", body: "El medidor se engaña con el interior oscuro. Aclara para que el vestido siga blanco." },
      { name: "Concierto / escenario con artista bajo reflector", ev: "−1 a −2 EV", body: "El medidor quiere aclarar el escenario oscuro; tu quieres lo oscuro. Resta." },
      { name: "Atardecer / hora dorada", ev: "−0.3 a −0.7 EV", body: "Subexposicion leve da colores mas ricos. Salva el cielo de quemarse." },
      { name: "Animal negro o tela oscura como sujeto", ev: "−0.7 a −1.3 EV", body: "Mantener el negro NEGRO, no gris." },
      { name: "Escenas de niebla / brumosas / nubladas", ev: "+0.3 a +0.7 EV", body: "La niebla se lee como 'muy brillante' para el medidor — sube para que el animo se vea aireado, no embarrado." },
      { name: "Cielo nocturno / fuegos artificiales / siluetas", ev: "−1 a −3 EV", body: "Abraza la oscuridad. Suelta el sesgo de 'gris medio' del medidor." },
    ],

    hardware_title: "Como aplicar +/− EV — por marca",
    hardware: [
      {
        name: "Canon",
        body: "Gira el dial Quick Control trasero en modos Av, Tv o P. O pulsa el boton dedicado [+/−] y gira el dial principal. La escala EV aparece en la parte inferior del visor o LCD — muestra tu compensacion actual.",
      },
      {
        name: "Nikon",
        body: "Manten pulsado el boton [+/−] (junto al disparador) y gira el dial trasero. El valor EV aparece en la LCD superior y en el visor. Algunos cuerpos tienen un dial EV dedicado en la parte superior.",
      },
      {
        name: "Sony",
        body: "La mayoria de los cuerpos tiene un DIAL dedicado de compensacion en la parte superior (bloqueado: pulsa el centro para desbloquear). O asigna EV a un boton personalizado. Las mirrorless muestran una previsualizacion en vivo que se actualiza al girar el dial — literalmente ves como quedara la foto.",
      },
      {
        name: "Fujifilm",
        body: "La mayoria de las Fuji tienen un dial EV dedicado en la parte superior (a veces marcado con valores −3 a +3). Algunos modelos nuevos ofrecen una posicion 'C' que permite ajustar EV con el dial trasero hasta ±5 EV.",
      },
    ],

    auto_mode_title: "En que modos funciona",
    auto_mode_body: "La Compensacion de Exposicion solo tiene sentido cuando la camara esta eligiendo alguna parte de la exposicion por ti:",
    auto_mode_table: [
      ["Programa (P)", "Si — la camara elige apertura Y obturacion; EV ajusta ambas"],
      ["Prioridad Apertura (Av/A)", "Si — tu eliges apertura, la camara elige obturacion; EV ajusta obturacion"],
      ["Prioridad Obturacion (Tv/S)", "Si — tu eliges obturacion, la camara elige apertura; EV ajusta apertura"],
      ["Manual (M) con Auto-ISO", "Si — la camara elige ISO; EV ajusta ISO. Muy potente para eventos."],
      ["Manual (M) ISO fijo", "NO — tu controlas todo. Ajusta apertura, obturacion o ISO directamente."],
    ],

    pro_tip:
      "Tip pro: no temas usar valores grandes. +2 EV en nieve es normal, no extremo. Los principiantes sub-compensan por miedo a arruinar la foto. Confia en el histograma, dial con confianza, y resetea a 0 entre sesiones para no subexponer tu proxima foto familiar por accidente.",

    workflow_title: "Tu flujo de trabajo diario",
    workflow_steps: [
      "Pon la camara en Prioridad Apertura (Av/A).",
      "Toma una foto de prueba.",
      "Revisa el histograma (y los Blinkies).",
      "Si hay clipping a la izquierda (sombras perdidas) → +EV. Si hay clipping a la derecha (altas luces perdidas) → −EV.",
      "Vuelve a disparar. Confirma que el histograma se ve correcto para el animo de la escena.",
      "Resetea a 0 EV cuando cambie la luz o pases a una escena nueva.",
    ],
  },
};

const SAMPLE_PHOTO = "/exposure/backlit.png";

export default function ExposureCompensationPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);
  const [ev, setEv] = useState(0);
  const filter = useMemo(() => filterForEV(ev), [ev]);
  const evLabel = useMemo(() => {
    const sign = ev > 0 ? "+" : ev < 0 ? "−" : "";
    const val = Math.abs(ev).toFixed(1);
    return ev === 0 ? "0 EV" : `${sign}${val} EV`;
  }, [ev]);

  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `exposure-compensation-${lang}.pdf`,
    [lang]
  );

  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black"
      data-testid="exposure-page"
    >
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          {/* Header */}
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
                {c.page_title}
              </h1>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="exposure-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>

          {/* The Why */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Warning weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.why_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.why_body}</p>
            <ul className="space-y-2">
              {c.why_examples.map((e, i) => (
                <li key={i} className="flex items-start gap-2 bg-[#0A0A0A] border border-white/10 rounded p-3">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-xs leading-relaxed">{e}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* The How */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <PlusMinus weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.how_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.how_body}</p>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
              <ul className="space-y-1.5">
                {c.how_units.map((u, i) => (
                  <li key={i} className="text-[#D4D4D8] text-xs font-['JetBrains_Mono']">{u}</li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Interactive slider */}
          <motion.div
            variants={itemAnim}
            className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/40 rounded-xl p-6"
            data-testid="exposure-interactive"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sliders weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.interactive_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-5">{c.interactive_intro}</p>

            {/* Before / After */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div>
                <p className="text-[#71717A] text-[10px] uppercase tracking-widest font-semibold mb-2 text-center">
                  {c.interactive_left}
                </p>
                <div className="aspect-[16/9] bg-black border border-white/10 rounded-lg overflow-hidden">
                  <img
                    src={SAMPLE_PHOTO}
                    alt="Original exposure"
                    className="w-full h-full object-cover"
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
              <div>
                <p className="text-[#F5A524] text-[10px] uppercase tracking-widest font-semibold mb-2 text-center">
                  {c.interactive_right} <span className="text-[#F5A524] font-['JetBrains_Mono']">{evLabel}</span>
                </p>
                <div className="aspect-[16/9] bg-black border border-[#F5A524]/30 rounded-lg overflow-hidden">
                  <img
                    src={SAMPLE_PHOTO}
                    alt={`Exposure adjusted ${evLabel}`}
                    className="w-full h-full object-cover transition-[filter] duration-100"
                    style={{ filter }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>

            {/* The slider */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-rose-400 text-sm font-['JetBrains_Mono']">−3</span>
                <input
                  type="range"
                  min={-3}
                  max={3}
                  step={0.3}
                  value={ev}
                  onChange={(e) => setEv(parseFloat(e.target.value))}
                  className="flex-1 accent-[#F5A524]"
                  data-testid="exposure-slider"
                  aria-label="Exposure compensation"
                />
                <span className="text-emerald-400 text-sm font-['JetBrains_Mono']">+3</span>
              </div>
              <div className="flex items-center justify-between text-[#71717A] text-[10px] font-['JetBrains_Mono']">
                <span>Darker</span>
                <button
                  onClick={() => setEv(0)}
                  className="text-[#F5A524] hover:underline text-xs"
                  data-testid="exposure-reset"
                >
                  Reset to 0 EV
                </button>
                <span>Brighter</span>
              </div>
            </div>

            {/* Mini histogram that shifts */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
              <p className="text-[#71717A] text-[10px] uppercase tracking-widest font-semibold mb-2 text-center">
                Histogram preview
              </p>
              <MiniHistogram ev={ev} />
              <p className="text-[#71717A] text-xs italic text-center mt-2">{c.interactive_caption}</p>
            </div>
          </motion.div>

          {/* Histogram connection */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <ChartBar weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.histogram_link_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.histogram_link_body}</p>
            <ul className="space-y-2 mb-3">
              {c.histogram_link_rules.map((r, i) => (
                <li key={i} className="flex items-start gap-2 bg-[#0A0A0A] border border-white/10 rounded p-3">
                  <ArrowsHorizontal weight="bold" className="w-4 h-4 text-[#F5A524] shrink-0 mt-0.5" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{r}</span>
                </li>
              ))}
            </ul>
            <div className="bg-rose-950/30 border border-rose-500/30 rounded-lg p-3">
              <p className="text-rose-300 text-xs leading-relaxed">{c.histogram_link_warning}</p>
            </div>
          </motion.div>

          {/* Real-world scenarios */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-4">{c.scenarios_title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {c.scenarios.map((s, i) => (
                <div
                  key={i}
                  className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3"
                  data-testid={`exposure-scenario-${i}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-white font-medium text-sm">{s.name}</p>
                    <p className="text-[#F5A524] font-['JetBrains_Mono'] text-xs whitespace-nowrap">{s.ev}</p>
                  </div>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Brand instructions */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Camera weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.hardware_title}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {c.hardware.map((b, i) => (
                <div
                  key={i}
                  className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3"
                  data-testid={`exposure-hw-${b.name.toLowerCase()}`}
                >
                  <p className="text-[#F5A524] font-medium text-sm mb-1.5">{b.name}</p>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">{b.body}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Auto modes */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-2">{c.auto_mode_title}</h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.auto_mode_body}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <tbody>
                  {c.auto_mode_table.map((row, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-2 pr-3 text-white font-medium align-top w-1/3">{row[0]}</td>
                      <td className="py-2 text-[#A1A1AA]">{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Workflow */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/40 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">{c.workflow_title}</h2>
            <ol className="space-y-2">
              {c.workflow_steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-xl p-4">
            <p className="text-[#F5A524] text-sm leading-relaxed italic">{c.pro_tip}</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Print */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="exposure-print-view">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">1. {c.why_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.why_body}</p>
        <ul className="list-disc ml-5 space-y-1 mb-4">{c.why_examples.map((e, i) => (<li key={i} className="text-xs text-gray-700">{e}</li>))}</ul>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">2. {c.how_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.how_body}</p>
        <ul className="list-disc ml-5 space-y-1 mb-4">{c.how_units.map((u, i) => (<li key={i} className="text-xs text-gray-700 font-mono">{u}</li>))}</ul>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">3. {c.histogram_link_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.histogram_link_body}</p>
        <ul className="list-disc ml-5 space-y-1 mb-2">{c.histogram_link_rules.map((r, i) => (<li key={i} className="text-xs text-gray-700">{r}</li>))}</ul>
        <p className="text-xs text-rose-700 italic mb-4">⚠ {c.histogram_link_warning}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">4. {c.scenarios_title}</h2>
        <table className="w-full text-xs border-collapse mb-4">
          <thead><tr className="bg-gray-100"><th className="border border-gray-300 p-2 text-left">Scene</th><th className="border border-gray-300 p-2 text-left">Set EV</th><th className="border border-gray-300 p-2 text-left">Why</th></tr></thead>
          <tbody>{c.scenarios.map((s, i) => (<tr key={i}><td className="border border-gray-300 p-2 font-semibold">{s.name}</td><td className="border border-gray-300 p-2 font-mono">{s.ev}</td><td className="border border-gray-300 p-2">{s.body}</td></tr>))}</tbody>
        </table>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">5. {c.hardware_title}</h2>
        <table className="w-full text-xs border-collapse mb-4">
          <tbody>{c.hardware.map((b, i) => (<tr key={i}><td className="border border-gray-300 p-2 font-semibold w-1/5">{b.name}</td><td className="border border-gray-300 p-2">{b.body}</td></tr>))}</tbody>
        </table>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">6. {c.auto_mode_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.auto_mode_body}</p>
        <table className="w-full text-xs border-collapse mb-4">
          <tbody>{c.auto_mode_table.map((row, i) => (<tr key={i}><td className="border border-gray-300 p-2 font-semibold w-1/3">{row[0]}</td><td className="border border-gray-300 p-2">{row[1]}</td></tr>))}</tbody>
        </table>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">7. {c.workflow_title}</h2>
        <ol className="list-decimal ml-5 space-y-1 mb-3">{c.workflow_steps.map((s, i) => (<li key={i} className="text-xs text-gray-700">{s}</li>))}</ol>
        <p className="text-xs text-gray-700 italic"><strong>Pro tip:</strong> {c.pro_tip}</p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
